import { useMemo, useState } from 'react';
import { ChevronDown, ChevronUp, ArrowUpDown } from 'lucide-react';
import { ResponseTable } from '../types';

type SortDir = 'asc' | 'desc';

interface Props {
  table: ResponseTable;
}

function isNumeric(value: unknown): boolean {
  if (typeof value === 'number') return true;
  if (typeof value === 'string' && value.trim() !== '') {
    return !Number.isNaN(Number(value.replace(/,/g, '')));
  }
  return false;
}

function columnIsNumeric(rows: ResponseTable['rows'], colIdx: number): boolean {
  let seen = 0;
  for (const row of rows) {
    const v = row[colIdx];
    if (v == null) continue;
    if (!isNumeric(v)) return false;
    seen++;
    if (seen >= 3) return true;
  }
  return seen > 0;
}

export default function AnswerTable({ table }: Props) {
  const [sortIdx, setSortIdx] = useState<number | null>(null);
  const [sortDir, setSortDir] = useState<SortDir>('asc');

  const numericCols = useMemo(
    () => table.columns.map((_, i) => columnIsNumeric(table.rows, i)),
    [table]
  );

  const sortedRows = useMemo(() => {
    if (sortIdx === null) return table.rows;
    const rows = [...table.rows];
    const numeric = numericCols[sortIdx];
    rows.sort((a, b) => {
      const av = a[sortIdx];
      const bv = b[sortIdx];
      if (av == null && bv == null) return 0;
      if (av == null) return 1;
      if (bv == null) return -1;
      let cmp = 0;
      if (numeric) {
        const an = typeof av === 'number' ? av : Number(String(av).replace(/,/g, ''));
        const bn = typeof bv === 'number' ? bv : Number(String(bv).replace(/,/g, ''));
        cmp = an - bn;
      } else {
        cmp = String(av).localeCompare(String(bv));
      }
      return sortDir === 'asc' ? cmp : -cmp;
    });
    return rows;
  }, [table.rows, sortIdx, sortDir, numericCols]);

  const handleSort = (idx: number) => {
    if (sortIdx === idx) {
      setSortDir((d) => (d === 'asc' ? 'desc' : 'asc'));
    } else {
      setSortIdx(idx);
      setSortDir('asc');
    }
  };

  return (
    <figure className="border border-neutral-200 rounded-lg overflow-hidden">
      {table.title && (
        <figcaption className="px-4 py-2 bg-neutral-50 border-b border-neutral-200 text-xs font-semibold text-neutral-700 uppercase tracking-wide">
          {table.title}
        </figcaption>
      )}
      <div className="overflow-x-auto">
        <table className="w-full text-sm">
          <thead className="bg-neutral-100 sticky top-0">
            <tr>
              {table.columns.map((col, idx) => (
                <th
                  key={`${col}-${idx}`}
                  scope="col"
                  className={`px-4 py-2 font-semibold text-neutral-700 cursor-pointer select-none hover:bg-neutral-200 ${
                    numericCols[idx] ? 'text-right' : 'text-left'
                  }`}
                  onClick={() => handleSort(idx)}
                  aria-sort={
                    sortIdx === idx ? (sortDir === 'asc' ? 'ascending' : 'descending') : 'none'
                  }
                >
                  <span className="inline-flex items-center gap-1">
                    {col}
                    {sortIdx === idx ? (
                      sortDir === 'asc' ? (
                        <ChevronUp className="w-3 h-3" />
                      ) : (
                        <ChevronDown className="w-3 h-3" />
                      )
                    ) : (
                      <ArrowUpDown className="w-3 h-3 opacity-30" />
                    )}
                  </span>
                </th>
              ))}
            </tr>
          </thead>
          <tbody className="divide-y divide-neutral-200">
            {sortedRows.map((row, i) => (
              <tr key={i} className="hover:bg-neutral-50">
                {row.map((cell, j) => (
                  <td
                    key={j}
                    className={`px-4 py-2 text-neutral-900 ${
                      numericCols[j] ? 'text-right font-mono tabular-nums' : ''
                    }`}
                  >
                    {cell === null || cell === undefined || cell === ''
                      ? '—'
                      : String(cell)}
                  </td>
                ))}
              </tr>
            ))}
          </tbody>
        </table>
      </div>
    </figure>
  );
}
